import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Grid,
  Button,
  Alert
} from '@mui/material';
import { Delete as DeleteIcon, ShoppingCart as ShoppingCartIcon } from '@mui/icons-material';
import { useWishlist } from '../../context/WishlistContext';
import { useCart } from '../../context/CartContext';
import { Product, getProductById } from '../../services/FirestoreService';
import { useAuth } from '../../context/AuthContext';

const WishlistSection: React.FC = () => {
  const { state, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();
  const { currentUser } = useAuth();
  const [wishlistProducts, setWishlistProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWishlistProducts = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const products = await Promise.all(
          state.items.map(async (item) => {
            const product = await getProductById(item.productId);
            return product;
          })
        );
        
        const validProducts = products.filter((product): product is Product => product !== null);
        setWishlistProducts(validProducts);
      } catch (err) {
        console.error('Error fetching wishlist products:', err);
        setError('Favori listenizdeki ürünler yüklenirken hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    fetchWishlistProducts();
  }, [state.items, currentUser]);

  const handleAddToCart = (product: Product) => {
    const cartItem = {
      id: product.id,
      name: product.name,
      price: product.price,
      discountedPrice: product.discountedPrice || null,
      quantity: 1,
      image: product.image,
      stock: product.stock
    };

    addToCart(cartItem);
  };

  if (!currentUser) {
    return (
      <Box sx={{ my: 4, textAlign: 'center' }}>
        <Typography variant="h6">Favori listeyi görüntülemek için lütfen giriş yapın.</Typography>
        <Button 
          variant="contained" 
          sx={{ mt: 2 }}
          onClick={() => window.location.href = '/login'}
        >
          Giriş Yap
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ my: 4, textAlign: 'center' }}>
        <Typography variant="h6">Favori listeniz yükleniyor...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ my: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Favori Listem ({wishlistProducts.length} ürün)
      </Typography>

      {wishlistProducts.length === 0 ? (
        <Typography>Favori listenizde henüz ürün bulunmamaktadır.</Typography>
      ) : (
        <Grid container spacing={{ xs: 1, sm: 2 }} columns={{ xs: 2, sm: 4, md: 6 }}>
          {wishlistProducts.map((product) => (
            <Grid key={product.id} size={{ xs: 1, sm: 2, md: 2 }} component="div">
              <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <CardMedia
                  component="img"
                  height="140"
                  image={product.image}
                  alt={product.name}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography gutterBottom variant="subtitle1" component="div">
                    {product.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ₺{product.discountedPrice ? product.discountedPrice : product.price}
                    {product.discountedPrice && (
                      <span style={{ marginLeft: '8px', textDecoration: 'line-through', color: 'gray' }}>
                        ₺{product.price}
                      </span>
                    )}
                  </Typography>
                </CardContent>
                <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between' }}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      const wishlistItem = state.items.find(item => item.productId === product.id);
                      if (wishlistItem) {
                        removeFromWishlist(wishlistItem.id).catch(console.error);
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleAddToCart(product)}
                  >
                    <ShoppingCartIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  )};

export default WishlistSection;